import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ACTION_REQUIRED_MAIL_TYPE_IDS } from '../dashboard.constants';
import { MasterService } from '../../core/services/master.service';
import { RebarAuthService } from '../../core/rebarauth/rebar.auth.service';

export interface ActionItem {
  id: number;
  reqId: number;
  label: string;
  date: string;
  expanded: boolean;
  dismissed: boolean;
  detail: string;
  actionText: string;
  linkLabel: string;
  linkUrl: string;
}

@Component({
  standalone: true,
  selector: 'app-action-required-dashboard',
  imports: [CommonModule],
  templateUrl: './action-required-dashboard.component.html',
  styleUrl: './action-required-dashboard.component.css',
})
export class ActionRequiredDashboardComponent {
  private _actions: ActionItem[] = [];

  @Output() loadingChange = new EventEmitter<boolean>();

  constructor(private masterService: MasterService, private authService: RebarAuthService) {}

  @Input() set rawItems(items: any[]) {
    this._actions = (items ?? [])
      .filter((item: any) =>
        ACTION_REQUIRED_MAIL_TYPE_IDS.includes(item.MailtypeID ?? item.MailTypeID ?? '')
      )
      .map((item: any, index: number) => ({
        id: item.ID ?? index + 1,
        reqId: item.ReqID ?? item.ReqId ?? 0,
        label: item.MailSubject ?? '',
        date: this.formatDate(item.MailSendDate ?? ''),
        expanded: false,
        dismissed: false,
        ...this.extractActionRequired(item.MailBody),
      }));
  }

  get visibleActions(): ActionItem[] {
    return this._actions.filter(a => !a.dismissed);
  }

  toggleExpand(item: ActionItem): void {
    item.expanded = !item.expanded;
  }

  dismiss(item: ActionItem, event: MouseEvent): void {
    event.stopPropagation();
    const updatedBy = `${this.authService.getUserName() ?? ''}@accenture.com`;
    this.loadingChange.emit(true);
    this.masterService.closeActionItem(item.reqId, updatedBy).subscribe({
      next: () => { item.dismissed = true; this.loadingChange.emit(false); },
      error: (err) => { console.error('Failed to close action item:', err); this.loadingChange.emit(false); },
    });
  }

  private formatDate(raw: string): string {
    if (!raw) return '';
    const d = new Date(raw);
    return isNaN(d.getTime()) ? raw : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }

  private extractActionRequired(html: string): { detail: string; actionText: string; linkLabel: string; linkUrl: string } {
    if (!html) return { detail: '', actionText: '', linkLabel: '', linkUrl: '' };
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const intro = doc.querySelector('p.intro');
    let detail = intro?.textContent?.trim() || '';
    if (!detail) {
      const tds = Array.from(doc.querySelectorAll('td'));
      // greeting ("Hi,", "Hi Team,", "Hi <name>,") and intro are in consecutive <td>s
      const hiIndex = tds.findIndex(td => /^Hi( [^,]+)?,$/.test(td.textContent?.trim() || ''));
      detail = hiIndex !== -1 ? tds[hiIndex + 1]?.textContent?.replace(/\s+/g, ' ').trim() || '' : '';
    }

    let actionText = '';
    const divActionText = doc.querySelector('.action-text');
    if (divActionText) {
      actionText = divActionText.textContent?.trim() || '';
    } else {
      const allTds = Array.from(doc.querySelectorAll('td'));
      const arIndex = allTds.findIndex(td => td.textContent?.trim().toUpperCase() === 'ACTION REQUIRED');
      if (arIndex !== -1 && allTds[arIndex + 1]) {
        actionText = allTds[arIndex + 1].textContent?.trim() || '';
      }
    }

    const anchors = Array.from(doc.querySelectorAll('a'));
    const anchor = anchors.find(a => a.classList.contains('btn-view')) ?? anchors[anchors.length - 1] ?? null;
    const linkLabel = anchor?.textContent?.trim() || '';
    const rawHref = anchor?.getAttribute('href') || '';

    let linkUrl = '';
    if (rawHref && rawHref !== '#') {
      try { linkUrl = new URL(rawHref).pathname + new URL(rawHref).search; } catch { linkUrl = rawHref; }
    }

    return { detail, actionText, linkLabel, linkUrl };
  }
}
